import { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import dashboardService from "../../services/DashboardService.js"
import RevenueChart from "./RevenueChart.js"
import {
  buildDateRange,
  formatCurrency,
  getGraphTitle,
  isCustomRangeIncomplete,
  mapRevenueGraph,
} from "./dashboardUtils.js"
import { Users, Package, Banknote, AlertTriangle, Wallet, Truck, LayoutDashboard, ChevronDown } from "lucide-react"
import { PageLayout, Button, StatusBadge } from "../common/index.js"

const AccountantDashboard = () => {
  const navigate = useNavigate()
  const [statsLoading, setStatsLoading] = useState(true)
  const [chartLoading, setChartLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(true)

  const [globalDateRange, setGlobalDateRange] = useState("this-month")
  const [customStartDate, setCustomStartDate] = useState("")
  const [customEndDate, setCustomEndDate] = useState("")
  const [openMenuId, setOpenMenuId] = useState(null)

  const [userStats, setUserStats] = useState({ total: 0, filtered: 0 })
  const [shipmentStats, setShipmentStats] = useState({ total: 0, filtered: 0 })
  const [revenueStats, setRevenueStats] = useState({ total: 0, filtered: 0, paid: 0 })
  const [dueStats, setDueStats] = useState({ total: 0, filtered: 0 })
  const [commissionStats, setCommissionStats] = useState({ total: 0, pending: 0, paid: 0, records: 0 })
  const [vehicleStats, setVehicleStats] = useState({ total: 0, filtered: 0 })

  const [recentShipments, setRecentShipments] = useState([])
  const [recentPayments, setRecentPayments] = useState([])
  const [recentCommissions, setRecentCommissions] = useState([])
  const [revenueData, setRevenueData] = useState([])
  const [graphGranularity, setGraphGranularity] = useState("monthly")

  const dateRange = useMemo(
    () => buildDateRange({ preset: globalDateRange, customStartDate, customEndDate }),
    [globalDateRange, customStartDate, customEndDate]
  )

  const customRangeIncomplete = isCustomRangeIncomplete(globalDateRange, customStartDate, customEndDate)

  // Overview fetch for KPI cards and revenue chart
  useEffect(() => {
    if (customRangeIncomplete) {
      setRevenueData([])
      setChartLoading(false)
      return
    }

    const fetchOverview = async () => {
      try {
        setStatsLoading(true)
        setChartLoading(true)
        const data = await dashboardService.getOverview({ fromDate: dateRange.from, toDate: dateRange.to })

        setUserStats({ total: data.users.total_active, filtered: data.users.total_active })
        setShipmentStats({ total: data.shipments.total_count, filtered: data.shipments.total_count })
        setRevenueStats({ total: data.revenue_due.total_revenue, filtered: data.revenue_due.total_revenue, paid: data.shipments.paid_amount })
        setDueStats({ total: data.revenue_due.total_due, filtered: data.revenue_due.total_due })
        setVehicleStats({ total: data.vehicles.total, filtered: data.vehicles.total })
        setCommissionStats({
          total: data.commission.grand_total.total,
          paid: data.commission.grand_total.paid,
          pending: data.commission.grand_total.pending,
          records: data.commission.total_records,
        })
        setRevenueData(mapRevenueGraph(data.revenue_graph))
        setGraphGranularity(data.graph_granularity || "monthly")
      } catch (error) {
        console.error("Error fetching dashboard overview:", error)
        setRevenueData([])
      } finally {
        setStatsLoading(false)
        setChartLoading(false)
      }
    }
    fetchOverview()
  }, [dateRange, customRangeIncomplete])

  // Recent shipments
  useEffect(() => {
    if (customRangeIncomplete) return

    const fetchRecentShipments = async () => {
      try {
        setTableLoading(true)
        const response = await dashboardService.getShipmentRecords({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          page: 1,
          perPage: 5,
        })
        setRecentShipments(response.shipments || [])
      } catch (error) {
        console.error("Error fetching recent shipments:", error)
      } finally {
        setTableLoading(false)
      }
    }
    fetchRecentShipments()
  }, [dateRange, customRangeIncomplete])

  // Recent payments
  useEffect(() => {
    if (customRangeIncomplete) return

    const fetchRecentPayments = async () => {
      try {
        const response = await dashboardService.getPaymentRecords({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          page: 1,
          perPage: 5,
        })
        setRecentPayments(response.payments || [])
      } catch (error) {
        console.error("Error fetching recent payments:", error)
      }
    }
    fetchRecentPayments()
  }, [dateRange, customRangeIncomplete])

  // Recent commissions
  useEffect(() => {
    if (customRangeIncomplete) return

    const fetchRecentCommissions = async () => {
      try {
        const response = await dashboardService.getCommissionRecords({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          page: 1,
          perPage: 5,
        })
        setRecentCommissions(response.commissions || [])
      } catch (error) {
        console.error("Error fetching recent commissions:", error)
      }
    }
    fetchRecentCommissions()
  }, [dateRange, customRangeIncomplete])

  return (
    <PageLayout
      title="Welcome, Accountant"
      icon={LayoutDashboard}
      actions={
        <>
          <div className="relative">
            <Button size="sm" icon={ChevronDown} iconPosition="right" onClick={() => setOpenMenuId(openMenuId === "quickLinks" ? null : "quickLinks")}>
              Quick Links
            </Button>
            {openMenuId === "quickLinks" && (
              <div className="absolute left-0 mt-2 w-44 bg-white border border-gray-200 rounded-card shadow-card z-20 py-1">
                <button onClick={() => { navigate("/shipments/add"); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-secondary">Book Shipment</button>
                <button onClick={() => { navigate("/daily-earnings"); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-secondary">Daily Earnings</button>
              </div>
            )}
          </div>
          <select
            value={globalDateRange}
            onChange={(e) => {
              setGlobalDateRange(e.target.value)
              if (e.target.value !== "custom") { setCustomStartDate(""); setCustomEndDate("") }
            }}
            className="input-clean w-auto text-sm py-1.5"
          >
            <option value="this-month">This Month</option>
            <option value="last-3-months">Last 3 Months</option>
            <option value="last-6-months">Last 6 Months</option>
            <option value="last-year">Last Year</option>
            <option value="all-time">All Time</option>
            <option value="custom">Custom</option>
          </select>
        </>
      }
    >
      {globalDateRange === "custom" && (
        <div className="mb-6 p-4 bg-white rounded-card shadow-card flex gap-4 items-end flex-wrap">
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">Start Date</label>
            <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="input-clean" />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">End Date</label>
            <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="input-clean" />
          </div>
        </div>
      )}

                {/* Stats Grid - 6 Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
                  {/* Card 1: Total Active Users */}
                  <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-lg transition-shadow relative min-h-[110px] lg:min-h-0">
                    <div className="flex mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">Active Users</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{userStats.filtered}</p>}
                      </div>
                    </div>
                    <div className="p-2 absolute rounded-md right-2 bottom-2 bg-brand">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                  </div>

                  {/* Card 2: Total Shipments */}
                  <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-lg transition-shadow relative min-h-[110px] lg:min-h-0">
                    <div className="flex mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">Shipments</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{shipmentStats.filtered}</p>}
                      </div>
                    </div>
                    <div className="p-2 absolute rounded-md right-2 bottom-2 bg-green-600">
                        <Package className="w-5 h-5 text-white" />
                      </div>
                  </div>

                  {/* Card 3: Total Revenue */}
                  <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-lg transition-shadow relative min-h-[110px] lg:min-h-0">
                    <div className="flex mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">Total Amount</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{formatCurrency(revenueStats.filtered)}</p>}
                        <p className="text-sm text-secondary mt-3 absolute font-semibold">Paid: {formatCurrency(revenueStats.paid)}</p>
                      </div>
                      <div className="bg-purple-500 p-2 absolute rounded-md right-2 bottom-2">
                        <Banknote className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Card 4: Total Due Amount */}
                  <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-lg transition-shadow relative min-h-[110px] lg:min-h-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">Outstanding</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{formatCurrency(dueStats.filtered)}</p>}
                      </div>
                      <div className="bg-blue-600 p-2 absolute rounded-md right-2 bottom-2">
                        <AlertTriangle className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Card 5: Commission */}
                  <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-lg transition-shadow relative min-h-[110px]">
                    <div className="flex mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">Commission</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{formatCurrency(commissionStats.pending)}</p>}
                        <p className="text-sm text-secondary mt-3 absolute font-semibold">Total: {formatCurrency(commissionStats.total)}</p>
                      </div>
                      <div className="bg-red-500 p-2 absolute rounded-md right-2 bottom-2">
                        <Wallet className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Card 6: Total Vehicles */}
                  <div className="bg-white rounded-card shadow-card p-4 hover:shadow-lg transition-shadow relative">
                    <div className="flex mb-3">
                      <div className="flex-1">
                        <p className="text-secondary text-xs font-medium">Vehicles</p>
                        {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{vehicleStats.filtered}</p>}
                      </div>
                      <div className="bg-indigo-500 p-2 absolute rounded-md right-2 bottom-2">
                        <Truck className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Revenue Graph and Recent Shipments */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                  {/* Revenue Graph */}
                  <div className="lg:col-span-2 bg-white rounded-card shadow-card p-4 md:p-6">
                    <h2 className="text-lg md:text-md font-semibold text-tertiary mb-4">
                      {getGraphTitle(graphGranularity)}
                    </h2>
                    {customRangeIncomplete ? (
                      <div className="flex items-center justify-center h-[320px] text-secondary text-sm">
                        Select both start and end dates to view the chart
                      </div>
                    ) : (
                      <RevenueChart data={revenueData} granularity={graphGranularity} loading={chartLoading} />
                    )}
                  </div>

                  {/* Recent Shipments */}
                  <div className="bg-white rounded-card shadow-card p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-md md:text-lg font-bold text-tertiary">Recent Shipments</h2>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/shipments")}>View All →</Button>
                    </div>
                    <div className="space-y-3">
                      {tableLoading ? (
                        <div className="flex justify-center py-6">
                          <div className="w-6 h-6 border-4 border-brand border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : recentShipments.length > 0 ? (
                        recentShipments.map((shipment) => (
                          <div
                            key={shipment.shipment_id || shipment.id}
                            className="p-3 bg-light rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                            onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-dark text-sm">{shipment.shipment_id}</p>
                                <p className="text-xs text-secondary">{shipment.sender_name}</p>
                              </div>
                              <StatusBadge status={shipment.status} />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-secondary text-sm text-center py-4">No recent shipments</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Recent Payments and Recent Commissions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Recent Payments */}
                  <div className="bg-white rounded-card shadow-card p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg md:text-xl font-bold text-dark">Recent Payments</h2>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/payment-history")}>View All →</Button>
                    </div>
                    <div className="space-y-3">
                      {recentPayments.length > 0 ? (
                        recentPayments.map((payment) => (
                          <div
                            key={payment.id}
                            className="p-3 bg-light rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                            onClick={() => navigate(`/payment-history/view/${payment.id}`)}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-dark text-sm">
                                  ₹{parseFloat(payment.payment_amount || 0).toLocaleString()}
                                </p>
                                <p className="text-xs text-secondary">Shipment: {payment.shipment_id}</p>
                                <p className="text-xs text-secondary">{payment.payment_mode}</p>
                              </div>
                              <p className="text-xs text-secondary">
                                {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : ''}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-secondary text-sm text-center py-4">No recent payments</p>
                      )}
                    </div>
                  </div>

                  {/* Recent Commissions (replaced Pending Returns) */}
                  <div className="bg-white rounded-card shadow-card p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg md:text-xl font-bold text-dark">Recent Commissions</h2>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/commissions")}>View All →</Button>
                    </div>
                    <div className="space-y-3">
                      {recentCommissions.length > 0 ? (
                        recentCommissions.map((comm) => (
                          <div
                            key={comm.commission_id || comm.id}
                            className="p-3 bg-light rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                            onClick={() => navigate(`/commissions/view/${comm.commission_id}`)}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold text-dark text-sm">{comm.commission_id}</p>
                                <p className="text-xs text-secondary">ML: {comm.ml_sheet_id}</p>
                              </div>
                              <StatusBadge status={comm.company_payment_status || 'pending'} />
                            </div>
                            <div className="flex justify-between text-xs text-secondary">
                              <span>{comm.company_name || ''}</span>
                              <span>{comm.owner_name || ''}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-secondary text-sm text-center py-4">No recent commissions</p>
                      )}
                    </div>
                  </div>
                </div>
    </PageLayout>
  )
}

export default AccountantDashboard;
